import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Lang } from '../../shared/dto/auth';
import type { MessageKey } from './registry';
import { locale, translate, type T, type Vars } from './translate';

export type { MessageKey };
export { locale, translate, type T, type Vars };

const LANG_KEY = 'kgr.lang';

export function storedLang(): Lang {
  try {
    const v = localStorage.getItem(LANG_KEY);
    if (v === 'is' || v === 'en') return v;
  } catch {
    /* storage unavailable */
  }
  return 'is';
}

interface I18nCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: T;
  loc: string;
}

const Ctx = createContext<I18nCtx | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(storedLang);
  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(LANG_KEY, l);
    } catch {
      /* ignore */
    }
  }, []);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  const value = useMemo<I18nCtx>(
    () => ({ lang, setLang, t: (k, v) => translate(lang, k, v), loc: locale(lang) }),
    [lang, setLang],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18nCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('I18nProvider missing');
  return c;
}

export function useT(): T {
  return useI18n().t;
}
